/**
 * Revision authorization checks for UX (UI visibility/hiding)
 *
 * IMPORTANT: Backend fully enforces authorization - these are UX checks only!
 *
 * Purpose: Hide unauthorized UI to improve user experience
 * - Shows/hides buttons, forms, sections based on role + obvious state
 * - Backend is the source of truth and will reject unauthorized actions
 * - Keep checks simple: only use role + data we already have in the frontend
 *
 * Philosophy: Aim to hide ~95% of unauthorized UI with simple checks.
 * The remaining ~5% that slips through is handled gracefully with backend
 * error responses (e.g., "Permission denied" toast messages).
 */

import {type Document, DocumentStatus} from '../../documents/types/document';
import {type Revision, RevisionStatus} from '../types';
import {type AuthUser} from '@/matic/types/auth';

/**
 * Can user view document?
 * Basic users can only view published; others can view all.
 */
export function canViewDocument(
  user: AuthUser | undefined,
  document: Document
): boolean {
  if (!user) return false;
  if (user.role === 'basic') {
    return document.status === DocumentStatus.PUBLISHED;
  }

  return true;
}

/**
 * Can user view revision history?
 * Same rules as viewing document.
 */
export function canViewRevisionHistory(
  user: AuthUser | undefined,
  document: Document
): boolean {
  return canViewDocument(user, document);
}

/**
 * Can user download this revision?
 * Basic users can only download published revisions.
 */
export function canDownloadRevision(
  user: AuthUser | undefined,
  revision: Revision
): boolean {
  if (!user) return false;
  if (user.role === 'basic') {
    return revision.isPublished;
  }

  return true;
}

/**
 * Can user create a new revision?
 *
 * Checks:
 * - Role (basic cannot, normal can for owned docs, quality+ can for any)
 * - Document status (must be approved/published)
 * - Ownership (for normal users)
 *
 * Backend also checks: approval states, locks, version conflicts, etc.
 */
export function canCreateRevision(
  user: AuthUser | undefined,
  document: Document
): boolean {
  if (!user) return false;

  // Can't create revisions for docs not yet approved

  if (
    document.status !== DocumentStatus.PUBLISHED &&
    document.status !== DocumentStatus.APPROVED
  ) {
    return false;
  }

  // Basic users cannot create revisions
  if (user.role === 'basic') return false;

  // Quality/Admin can create for any document
  if (user.role === 'quality' || user.role === 'admin') return true;

  // Normal users can only create for owned documents
  return user.id === document.createdBy;
}

/**
 * Can user edit this revision?
 *
 * Checks:
 * - Status (only draft revisions can be edited)
 * - Role (basic cannot, normal can for owned docs, quality+ can for any)
 * - Ownership (for normal users)
 */
export function canEditRevision(
  user: AuthUser | undefined,
  revision: Revision,
  document: Document
): boolean {
  if (!user) return false;

  // Can only edit draft revisions
  if (revision.status !== RevisionStatus.DRAFT) return false;

  // Basic users cannot edit
  if (user.role === 'basic') return false;

  // Quality/Admin can edit any draft
  if (user.role === 'quality' || user.role === 'admin') return true;

  // Normal users can only edit drafts for owned documents
  return user.id === document.createdBy;
}

/**
 * Can user publish a revision?
 * Only quality+ can publish (backend checks approval status).
 */
export function canPublishRevision(user: AuthUser | undefined): boolean {
  if (!user) return false;
  return user.role === 'quality' || user.role === 'admin';
}

/**
 * Can user revoke a revision (In Review → Draft)?
 * Same ownership rules as editing.
 */
export function canRevokeRevision(
  user: AuthUser | undefined,
  revision: Revision,
  document: Document
): boolean {
  if (!user) return false;

  // Can only revoke in_review revisions
  if (revision.status !== RevisionStatus.IN_REVIEW) return false;

  // Basic users cannot revoke
  if (user.role === 'basic') return false;

  // Quality/Admin can revoke any
  if (user.role === 'quality' || user.role === 'admin') return true;

  // Normal users can only revoke for owned documents
  return user.id === document.createdBy;
}

/**
 * Can user discard a draft revision?
 *
 * Use this when user wants to delete an accidental draft and revert
 * to the previous revision state.
 *
 * Requirements:
 * - Revision must be in DRAFT status
 * - Document must have more than one revision (can't discard the only revision)
 * - Only Admin/Quality roles can discard (backend enforces)
 */
export function canDiscardDraftRevision(
  user: AuthUser | undefined,
  revision: Revision,
  document: Document,
  totalRevisions: number
): boolean {
  if (!user) return false;

  // Can only discard draft revisions
  if (revision.status !== RevisionStatus.DRAFT) return false;

  // Can't discard if it's the only revision (use delete document instead)
  if (totalRevisions <= 1) return false;

  // Only Admin/Quality can discard drafts
  return user.role === 'quality' || user.role === 'admin';
}

/**
 * Can user cancel an approval submission?
 *
 * This revokes the active approval workflow and reverts the revision
 * from IN_REVIEW back to DRAFT.
 *
 * Alias for canRevokeRevision with clearer naming for the UI.
 */
export function canCancelSubmission(
  user: AuthUser | undefined,
  revision: Revision,
  document: Document
): boolean {
  return canRevokeRevision(user, revision, document);
}
